"use client";
import React from "react";
import { MapPin, FlaskConical, Lock, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const Locations = () => {
  return (
    <section className="bg-[#fcfcfc] py-16 px-6 md:py-24">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side: Map Visual */}
        <div className="relative flex justify-center items-center">
          {/* Main Map Image */}
          <div className="relative w-full max-w-xl">
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} // Only triggers the first time it enters view
              transition={{ type: "spring", stiffness: 100 }}
            >
              <img
                src="images/usa-map-locations.png"
                alt="Network Map"
                className="w-full h-auto opacity-50"
              />
            </motion.div>
            {/* Floating Stats Card */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} // Only triggers the first time it enters view
              transition={{ type: "spring", stiffness: 100 }}
            >
              <div className="absolute bottom-4 left-0 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/50 p-6 rounded-2xl shadow-xl max-w-[250px]">
                <p className="text-3xl font-bold text-gray-900 leading-none">
                  2,200+
                </p>
                <p className="text-sm font-semibold text-gray-600 mt-1">
                  Partner Locations
                </p>
                <p className="text-xs text-gray-500 font-medium mt-2 tracking-wide">
                  In Most States
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Side: Content */}
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-gray-500 font-normal tracking-widest uppercase text-sm">
              Nationwide Access
            </h3>
            <h2 className="text-4xl font-bold text-[#1A1A1A] leading-tight">
              With{" "}
              <span className="text-orange-500 font-extrabold">
                2,200+ Locations Nationwide,
              </span>{" "}
              Care Is Always Within Reach
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed max-w-xl">
              From major cities to local communities, FeelGood partners with
              trusted labs across the country—so you can get tested without long
              travel, long waits, or added stress.
            </p>
          </div>

          {/* Feature List */}
          <ul className="space-y-5">
            <li className="flex items-center gap-4">
              <div className="bg-orange-100 p-2 rounded-lg">
                <MapPin className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-gray-800 font-medium text-lg">
                Local lab partners nationwide
              </span>
            </li>
            <li className="flex items-center gap-4">
              <div className="bg-orange-100 p-2 rounded-lg">
                <FlaskConical className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-gray-800 font-medium text-lg">
                Clinically validated testing
              </span>
            </li>
            <li className="flex items-center gap-4">
              <div className="bg-orange-100 p-2 rounded-lg">
                <Lock className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-gray-800 font-medium text-lg">
                Secure & private results
              </span>
            </li>
          </ul>

          {/* CTA Button */}
          <a href="/start" className="group inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-orange-200/50 transition-all active:scale-95">
            Find a Location Near You
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Locations;
